import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { PageResult } from '@/lib/api'

/** Admin endpoints return either a bare array (`GET /admins`) or a page. */
type Rows<T> = T[] | PageResult<T>

interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[]
  /**
   * The screen's query. DataTable renders the loading, error and empty states
   * itself so all seven list screens present them identically.
   */
  query: UseQueryResult<Rows<T>>
  emptyMessage?: string
  /**
   * True when the screen's own filters are narrowing the list. Without it an
   * empty filter result renders `emptyMessage` — "No messages in the catalog
   * yet." over a catalog of 413 — which reads as data loss rather than a filter
   * that matched nothing. `isSearching` only covers the search box, so a screen
   * with dropdown filters has to say so.
   */
  isFiltered?: boolean
  /** Client-side filter across every column. Use only for unpaginated lists. */
  searchPlaceholder?: string
  /**
   * Server-side search. Required whenever `pagination` is set — a client-side
   * filter over a server-paginated list would only search the visible page,
   * which reads as "no results" for anything further down.
   */
  search?: { value: string; onChange: (value: string) => void; placeholder?: string }
  /** Server-side pagination. Omit to page through the fetched rows in-browser. */
  pagination?: { page: number; onPageChange: (page: number) => void }
  /**
   * Row selection, for screens with a bulk action. Passing this prepends a
   * checkbox column — the screen does not define one, so every selectable list
   * gets the same column in the same place.
   *
   * `rowId` must be **stable and unique per row**, not the array index: rows
   * are keyed by it, so an index would move selection onto a different record
   * when the list refetches or reorders.
   *
   * The header checkbox toggles **only the current page**. That is deliberate
   * for a queue whose rows write points — an operator should not be able to
   * action rows they never loaded.
   */
  selection?: {
    rowId: (row: T) => string
    selected: string[]
    onChange: (ids: string[]) => void
  }
}

const CLIENT_PAGE_SIZE = 20

function unwrap<T>(payload: Rows<T> | undefined): { rows: T[]; total: number; pageSize: number } {
  if (!payload) return { rows: [], total: 0, pageSize: CLIENT_PAGE_SIZE }
  if (Array.isArray(payload)) return { rows: payload, total: payload.length, pageSize: CLIENT_PAGE_SIZE }
  return { rows: payload.list, total: payload.total, pageSize: payload.page_size || CLIENT_PAGE_SIZE }
}

/**
 * The one table behind every list view. Owns sorting, its own three states, and
 * either mode of pagination.
 */
export function DataTable<T>({
  columns,
  query,
  emptyMessage = 'Nothing here yet.',
  isFiltered = false,
  searchPlaceholder,
  search,
  pagination,
  selection,
}: DataTableProps<T>) {
  const [globalFilter, setGlobalFilter] = useState('')
  const { rows, total, pageSize } = unwrap(query.data)
  const isServerPaged = pagination !== undefined
  const pageCount = isServerPaged ? Math.max(1, Math.ceil(total / pageSize)) : undefined

  // TanStack keys selection by row id; the prop is a plain array because that is
  // what a screen wants to iterate when firing the bulk action.
  const rowSelection = useMemo(
    () => Object.fromEntries((selection?.selected ?? []).map((id) => [id, true])),
    [selection?.selected],
  )

  const allColumns = useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!selection) return columns
    return [
      {
        id: '__select',
        enableSorting: false,
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
            aria-label="Select every row on this page"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={(value) => row.toggleSelected(Boolean(value))}
            aria-label="Select row"
          />
        ),
      },
      ...columns,
    ]
  }, [columns, selection])

  const table = useReactTable({
    data: rows,
    columns: allColumns,
    getRowId: selection ? (row) => selection.rowId(row) : undefined,
    enableRowSelection: Boolean(selection),
    state: {
      ...(isServerPaged ? {} : { globalFilter }),
      ...(selection ? { rowSelection } : {}),
    },
    onRowSelectionChange: selection
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(rowSelection) : updater
          selection.onChange(Object.keys(next).filter((id) => next[id]))
        }
      : undefined,
    onGlobalFilterChange: isServerPaged ? undefined : setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Server-paged rows arrive pre-sliced, so filtering and paging them again
    // in the browser would fight the backend.
    ...(isServerPaged
      ? { manualPagination: true, manualFiltering: true, pageCount }
      : {
          getFilteredRowModel: getFilteredRowModel(),
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageSize: CLIENT_PAGE_SIZE } },
        }),
  })

  const visibleRows = table.getRowModel().rows
  const isSearching = Boolean(search ? search.value : globalFilter)

  return (
    <div className="space-y-3">
      {search ? (
        <Input
          value={search.value}
          onChange={(event) => search.onChange(event.target.value)}
          placeholder={search.placeholder ?? 'Search…'}
          className="max-w-xs"
          aria-label={search.placeholder ?? 'Search'}
        />
      ) : (
        searchPlaceholder && (
          <Input
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            placeholder={searchPlaceholder}
            className="max-w-xs"
            aria-label={searchPlaceholder}
          />
        )
      )}

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sortable = header.column.getCanSort()
                const sorted = header.column.getIsSorted()
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' ? (
                          <ArrowUp className="size-3" />
                        ) : sorted === 'desc' ? (
                          <ArrowDown className="size-3" />
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {query.isPending ? (
            Array.from({ length: 5 }, (_, rowIndex) => (
              <TableRow key={`skeleton-${rowIndex}`}>
                {allColumns.map((_column, cellIndex) => (
                  <TableCell key={`skeleton-${rowIndex}-${cellIndex}`}>
                    <Skeleton className="h-4 w-full max-w-40" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : query.isError ? (
            <TableRow>
              <TableCell colSpan={allColumns.length} className="py-10 text-center">
                <p className="text-sm text-destructive">
                  Could not load this list — {(query.error as Error).message}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => void query.refetch()}
                >
                  Try again
                </Button>
              </TableCell>
            </TableRow>
          ) : visibleRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={allColumns.length} className="py-10 text-center text-muted-foreground">
                {isSearching || isFiltered ? 'No matches.' : emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            visibleRows.map((row) => (
              <TableRow key={row.id} className="hover:bg-secondary/60">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {pagination
        ? pageCount !== undefined &&
          pageCount > 1 && (
            <Pager
              page={pagination.page}
              pageCount={pageCount}
              busy={query.isFetching}
              onChange={pagination.onPageChange}
            />
          )
        : table.getPageCount() > 1 && (
            <Pager
              page={table.getState().pagination.pageIndex + 1}
              pageCount={table.getPageCount()}
              busy={false}
              onChange={(next) => table.setPageIndex(next - 1)}
            />
          )}
    </div>
  )
}

function Pager({
  page,
  pageCount,
  busy,
  onChange,
}: {
  page: number
  pageCount: number
  busy: boolean
  onChange: (page: number) => void
}) {
  return (
    <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground">
      <span>
        Page {page} of {pageCount}
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onChange(page - 1)}
        disabled={busy || page <= 1}
      >
        Previous
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onChange(page + 1)}
        disabled={busy || page >= pageCount}
      >
        Next
      </Button>
    </div>
  )
}
